<template>
  <div class="services-page">
    <!-- Hero Section with DNA Animation Background -->
    <section class="services-hero">
      <!-- Background Elements -->
      <div class="services-bg-elements">
        <div class="services-bg-shape-1"></div>
        <div class="services-bg-shape-2"></div>
        <div class="services-bg-dots"></div>
      </div>
      
      <!-- Content Container -->
      <div class="container position-relative">
        <div class="row hero-content-row">
          <!-- Hero Content -->
          <div class="col-lg-8 mx-auto text-center" data-aos="fade-up">
            <div class="section-badge mb-3">
              <span class="badge-icon"><i class="bi bi-gear"></i></span>
              <span class="badge-text">OUR EXPERTISE</span>
            </div>
            
            <h1 class="section-title mb-3">
              Specialized <span class="text-gradient">IP Services</span> for Biotech Innovation
            </h1>
            
            <p class="section-subtitle mb-4">
              Strategic intellectual property solutions tailored for the unique challenges of biotechnology and pharmaceutical industries
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- Services Overview Section -->
    <section class="services-overview-section">
      <div class="container">
        <div class="row mb-5">
          <div class="col-lg-10 mx-auto text-center">
            <p class="lead">
              At PEIST Biotech IP Consulting, we provide comprehensive intellectual property services designed to protect your innovations, maximize their value, and support your business objectives in the competitive biotech landscape.
            </p>
          </div>
        </div>

        <!-- Services Cards Grid -->
        <div class="services-grid">
          <!-- Service 1: Patent Strategy -->
          <div class="service-card" data-aos="fade-up" data-aos-delay="0">
            <div class="service-icon">
              <i class="bi bi-lightbulb"></i>
            </div>
            <h3 class="service-title">Patent Strategy & Portfolio Management</h3>
            <p class="service-description">
              Develop a comprehensive patent strategy aligned with your business goals. We help identify patentable innovations and build a strategic portfolio.
            </p>
            <ul class="service-features">
              <li><i class="bi bi-check-circle"></i> Patentability assessments</li>
              <li><i class="bi bi-check-circle"></i> Portfolio development</li>
              <li><i class="bi bi-check-circle"></i> Freedom-to-operate analysis</li>
              <li><i class="bi bi-check-circle"></i> Patent landscape analysis</li>
            </ul>
            <div class="service-link">
              <a href="#" @click.prevent="scrollToContact">Learn More <i class="bi bi-arrow-right"></i></a>
            </div>
          </div>

          <!-- Service 2: IP Due Diligence -->
          <div class="service-card" data-aos="fade-up" data-aos-delay="100">
            <div class="service-icon">
              <i class="bi bi-file-earmark-text"></i>
            </div>
            <h3 class="service-title">IP Due Diligence</h3>
            <p class="service-description">
              Comprehensive analysis of intellectual property assets for investments, partnerships, mergers, and acquisitions to inform your business decisions.
            </p>
            <ul class="service-features">
              <li><i class="bi bi-check-circle"></i> IP asset valuation</li>
              <li><i class="bi bi-check-circle"></i> Portfolio strength assessment</li>
              <li><i class="bi bi-check-circle"></i> Risk assessment strategies</li>
              <li><i class="bi bi-check-circle"></i> Investment decision support</li>
            </ul>
            <div class="service-link">
              <a href="#" @click.prevent="scrollToContact">Learn More <i class="bi bi-arrow-right"></i></a>
            </div>
          </div>

          <!-- Service 3: Regulatory Compliance -->
          <div class="service-card" data-aos="fade-up" data-aos-delay="200">
            <div class="service-icon">
              <i class="bi bi-clipboard-check"></i>
            </div>
            <h3 class="service-title">Regulatory Compliance & IP Strategy</h3>
            <p class="service-description">
              Navigate the complex intersection of regulatory requirements and intellectual property protection for drugs, biologics, and medical devices.
            </p>
            <ul class="service-features">
              <li><i class="bi bi-check-circle"></i> FDA/EMA pathway analysis</li>
              <li><i class="bi bi-check-circle"></i> Patent term extension strategies</li>
              <li><i class="bi bi-check-circle"></i> Exclusivity optimization</li>
              <li><i class="bi bi-check-circle"></i> Data exclusivity strategies</li>
            </ul>
            <div class="service-link">
              <a href="#" @click.prevent="scrollToContact">Learn More <i class="bi bi-arrow-right"></i></a>
            </div>
          </div>

          <!-- Service 4: Licensing -->
          <div class="service-card" data-aos="fade-up" data-aos-delay="0">
            <div class="service-icon">
              <i class="bi bi-people"></i>
            </div>
            <h3 class="service-title">Licensing & Collaboration Agreements</h3>
            <p class="service-description">
              Strategic guidance on structuring and negotiating IP-related agreements to maximize the value of your intellectual property.
            </p>
            <ul class="service-features">
              <li><i class="bi bi-check-circle"></i> License agreement structuring</li>
              <li><i class="bi bi-check-circle"></i> IP ownership allocation</li>
              <li><i class="bi bi-check-circle"></i> Royalty structures</li>
              <li><i class="bi bi-check-circle"></i> Technology transfer guidance</li>
            </ul>
            <div class="service-link">
              <a href="#" @click.prevent="scrollToContact">Learn More <i class="bi bi-arrow-right"></i></a>
            </div>
          </div>

          <!-- Service 5: Competitive Intelligence -->
          <div class="service-card" data-aos="fade-up" data-aos-delay="100">
            <div class="service-icon">
              <i class="bi bi-search"></i>
            </div>
            <h3 class="service-title">Competitive Intelligence</h3>
            <p class="service-description">
              Gain insights into competitors' IP strategies and technological developments to inform your R&D and business strategies.
            </p>
            <ul class="service-features">
              <li><i class="bi bi-check-circle"></i> Competitor patent monitoring</li>
              <li><i class="bi bi-check-circle"></i> Technology trend analysis</li>
              <li><i class="bi bi-check-circle"></i> White space identification</li>
              <li><i class="bi bi-check-circle"></i> R&D direction guidance</li>
            </ul>
            <div class="service-link">
              <a href="#" @click.prevent="scrollToContact">Learn More <i class="bi bi-arrow-right"></i></a>
            </div>
          </div>

          <!-- Service 6: IP Litigation Support -->
          <div class="service-card" data-aos="fade-up" data-aos-delay="200">
            <div class="service-icon">
              <i class="bi bi-briefcase"></i>
            </div>
            <h3 class="service-title">IP Litigation Support</h3>
            <p class="service-description">
              Expert support for intellectual property disputes and litigation to strengthen your position in IP conflicts.
            </p>
            <ul class="service-features">
              <li><i class="bi bi-check-circle"></i> Technical analysis</li>
              <li><i class="bi bi-check-circle"></i> Prior art research</li>
              <li><i class="bi bi-check-circle"></i> Expert witness coordination</li>
              <li><i class="bi bi-check-circle"></i> Settlement strategy guidance</li>
            </ul>
            <div class="service-link">
              <a href="#" @click.prevent="scrollToContact">Learn More <i class="bi bi-arrow-right"></i></a>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Our Process Section -->
    <section class="process-section">
      <div class="process-bg-elements">
        <div class="process-bg-shape-1"></div>
        <div class="process-bg-shape-2"></div>
      </div>
      <div class="container position-relative">
        <div class="row mb-5">
          <div class="col-lg-8 mx-auto text-center">
            <div class="section-badge mb-3" data-aos="fade-up">
              <span class="badge-icon"><i class="bi bi-diagram-3"></i></span>
              <span class="badge-text">OUR APPROACH</span>
            </div>
            <h2 class="section-title mb-3" data-aos="fade-up">
              A <span class="text-gradient">Systematic Approach</span> to IP Strategy
            </h2>
            <p class="section-subtitle" data-aos="fade-up">
              Our proven methodology ensures your intellectual property strategy aligns perfectly with your business objectives
            </p>
          </div>
        </div>

        <div class="process-timeline">
          <!-- Step 1 -->
          <div class="process-step" data-aos="fade-up">
            <div class="process-number">01</div>
            <div class="process-content">
              <h3 class="process-title">Initial Consultation</h3>
              <p class="process-description">
                We begin with a thorough discussion of your business goals, technology portfolio, and IP needs to understand your unique challenges and opportunities.
              </p>
              <div class="process-icon">
                <i class="bi bi-chat-dots"></i>
              </div>
            </div>
          </div>

          <!-- Step 2 -->
          <div class="process-step" data-aos="fade-up" data-aos-delay="100">
            <div class="process-number">02</div>
            <div class="process-content">
              <h3 class="process-title">Assessment & Analysis</h3>
              <p class="process-description">
                Our team conducts a comprehensive analysis of your current IP position, identifying strengths, weaknesses, opportunities, and potential risks.
              </p>
              <div class="process-icon">
                <i class="bi bi-clipboard-data"></i>
              </div>
            </div>
          </div>

          <!-- Step 3 -->
          <div class="process-step" data-aos="fade-up" data-aos-delay="200">
            <div class="process-number">03</div>
            <div class="process-content">
              <h3 class="process-title">Strategy Development</h3>
              <p class="process-description">
                We create a customized IP strategy aligned with your business objectives, market position, and competitive landscape.
              </p>
              <div class="process-icon">
                <i class="bi bi-gear"></i>
              </div>
            </div>
          </div>

          <!-- Step 4 -->
          <div class="process-step" data-aos="fade-up" data-aos-delay="300">
            <div class="process-number">04</div>
            <div class="process-content">
              <h3 class="process-title">Implementation & Support</h3>
              <p class="process-description">
                We provide ongoing guidance and support as you execute your IP strategy, adapting to changing business needs and market conditions.
              </p>
              <div class="process-icon">
                <i class="bi bi-rocket-takeoff"></i>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Call to Action Section -->
    <section class="cta-section" id="contact">
      <div class="container">
        <div class="cta-card" data-aos="fade-up">
          <div class="row align-items-center">
            <div class="col-lg-8">
              <h2 class="cta-title">Ready to Protect Your Biotech Innovations?</h2>
              <p class="cta-text">
                Contact us today to discuss how our specialized IP consulting services can help secure and maximize the value of your biotech and pharmaceutical innovations.
              </p>
            </div>
            <div class="col-lg-4 text-lg-end mt-4 mt-lg-0">
              <router-link to="/contact" class="btn btn-primary btn-lg cta-button">
                Schedule a Consultation
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
export default {
  name: 'ServicesPage',
  methods: {
    scrollToContact() {
      document.getElementById('contact').scrollIntoView({ behavior: 'smooth' });
    }
  }
}
</script>

<style scoped>
/* Hero Section Styles */
.services-page {
  overflow-x: hidden;
}

.services-hero {
  position: relative;
  padding: 120px 0 80px;
  background-color: var(--bs-light);
  overflow: hidden;
}

.services-bg-elements {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
}

.services-bg-shape-1 {
  position: absolute;
  top: -150px;
  right: -100px;
  width: 500px;
  height: 500px;
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(13, 110, 253, 0.1), rgba(13, 110, 253, 0.05));
  z-index: -1;
}

.services-bg-shape-2 {
  position: absolute;
  bottom: -100px;
  left: -100px;
  width: 400px;
  height: 400px;
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(13, 110, 253, 0.08), rgba(13, 110, 253, 0.03));
  z-index: -1;
}

.services-bg-dots {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: radial-gradient(rgba(13, 110, 253, 0.1) 1px, transparent 1px);
  background-size: 20px 20px;
  z-index: -1;
}

.section-badge {
  display: inline-flex;
  align-items: center;
  padding: 8px 16px;
  background-color: rgba(13, 110, 253, 0.1);
  border-radius: 30px;
  margin-bottom: 20px;
}

.badge-icon {
  margin-right: 8px;
  color: #0d6efd;
}

.badge-text {
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 1px;
  color: #0d6efd;
}

.section-title {
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  line-height: 1.2;
}

.text-gradient {
  background: linear-gradient(90deg, #0d6efd, #6610f2);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-fill-color: transparent;
}

.section-subtitle {
  font-size: 1.2rem;
  color: #6c757d;
  max-width: 800px;
  margin: 0 auto;
}

/* Services Overview Section */
.services-overview-section {
  padding: 80px 0;
}

.services-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 30px;
  margin-top: 40px;
}

.service-card {
  background-color: #fff;
  border-radius: 12px;
  padding: 30px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.service-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.1);
}

.service-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 5px;
  height: 100%;
  background: linear-gradient(to bottom, #0d6efd, #6610f2);
  border-radius: 5px 0 0 5px;
}

.service-icon {
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(13, 110, 253, 0.1);
  border-radius: 12px;
  margin-bottom: 20px;
}

.service-icon i {
  font-size: 28px;
  color: #0d6efd;
}

.service-title {
  font-size: 1.4rem;
  font-weight: 700;
  margin-bottom: 15px;
  color: #212529;
}

.service-description {
  color: #6c757d;
  margin-bottom: 20px;
  flex-grow: 1;
}

.service-features {
  list-style: none;
  padding: 0;
  margin-bottom: 20px;
}

.service-features li {
  display: flex;
  align-items: flex-start;
  margin-bottom: 10px;
  color: #495057;
}

.service-features li i {
  color: #0d6efd;
  margin-right: 10px;
  font-size: 16px;
  margin-top: 4px;
}

.service-link a {
  color: #0d6efd;
  text-decoration: none;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  transition: all 0.3s ease;
}

.service-link a i {
  margin-left: 5px;
  transition: transform 0.3s ease;
}

.service-link a:hover {
  color: #0a58ca;
}

.service-link a:hover i {
  transform: translateX(5px);
}

/* Process Section Styles */
.process-section {
  padding: 100px 0;
  position: relative;
  background-color: #f8f9fa;
  overflow: hidden;
}

.process-bg-elements {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
}

.process-bg-shape-1 {
  position: absolute;
  top: -100px;
  right: -100px;
  width: 400px;
  height: 400px;
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(13, 110, 253, 0.08), rgba(13, 110, 253, 0.03));
  z-index: -1;
}

.process-bg-shape-2 {
  position: absolute;
  bottom: -100px;
  left: -100px;
  width: 300px;
  height: 300px;
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(13, 110, 253, 0.05), rgba(13, 110, 253, 0.02));
  z-index: -1;
}

.process-timeline {
  position: relative;
  max-width: 1000px;
  margin: 0 auto;
  padding-top: 40px;
}

.process-timeline::before {
  content: '';
  position: absolute;
  top: 0;
  left: 50px;
  height: 100%;
  width: 2px;
  background: linear-gradient(to bottom, #0d6efd, rgba(13, 110, 253, 0.3));
  z-index: 1;
}

.process-step {
  position: relative;
  padding-left: 100px;
  margin-bottom: 60px;
}

.process-step:last-child {
  margin-bottom: 0;
}

.process-number {
  position: absolute;
  left: 0;
  top: 0;
  width: 50px;
  height: 50px;
  background: linear-gradient(135deg, #0d6efd, #6610f2);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 1.2rem;
  z-index: 2;
}

.process-content {
  background-color: white;
  border-radius: 12px;
  padding: 30px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  position: relative;
}

.process-title {
  font-size: 1.4rem;
  font-weight: 700;
  margin-bottom: 15px;
  color: #212529;
}

.process-description {
  color: #6c757d;
  margin-bottom: 0;
}

.process-icon {
  position: absolute;
  right: 30px;
  top: 30px;
  color: rgba(13, 110, 253, 0.2);
  font-size: 2rem;
}

/* Call to Action Section Styles */
.cta-section {
  padding: 80px 0;
}

.cta-card {
  background: linear-gradient(135deg, #0d6efd, #6610f2);
  border-radius: 12px;
  padding: 50px;
  color: white;
  box-shadow: 0 15px 35px rgba(13, 110, 253, 0.2);
  position: relative;
  overflow: hidden;
}

.cta-card::before {
  content: '';
  position: absolute;
  top: -50px;
  right: -50px;
  width: 200px;
  height: 200px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  z-index: 1;
}

.cta-card::after {
  content: '';
  position: absolute;
  bottom: -50px;
  left: -50px;
  width: 150px;
  height: 150px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  z-index: 1;
}

.cta-title {
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 15px;
  position: relative;
  z-index: 2;
}

.cta-text {
  font-size: 1.1rem;
  margin-bottom: 0;
  opacity: 0.9;
  position: relative;
  z-index: 2;
}

.cta-button {
  background-color: white;
  color: #0d6efd;
  border: none;
  padding: 12px 30px;
  font-weight: 600;
  border-radius: 30px;
  transition: all 0.3s ease;
  position: relative;
  z-index: 2;
}

.cta-button:hover {
  background-color: rgba(255, 255, 255, 0.9);
  transform: translateY(-3px);
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
}

/* Responsive Adjustments */
@media (max-width: 991.98px) {
  .services-hero {
    padding: 80px 0 60px;
  }
  
  .section-title {
    font-size: 2rem;
  }
  
  .services-grid {
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  }
  
  .process-timeline::before {
    left: 30px;
  }
  
  .process-step {
    padding-left: 70px;
  }
  
  .process-number {
    width: 40px;
    height: 40px;
    font-size: 1rem;
  }
  
  .cta-card {
    padding: 30px;
  }
  
  .cta-title {
    font-size: 1.6rem;
  }
}

@media (max-width: 767.98px) {
  .services-hero {
    padding: 60px 0 40px;
  }
  
  .section-title {
    font-size: 1.8rem;
  }
  
  .section-subtitle {
    font-size: 1rem;
  }
  
  .services-grid {
    grid-template-columns: 1fr;
  }
  
  .process-icon {
    display: none;
  }
}
</style>
